import {
  coerceDate,
  coerceNumeric,
  parseExcelWorkbook,
} from "@1823-partners/core";

export const SOURCE_TYPES = {
  BAML: "BAML",
  BAML_CLO: "BAML_CLO",
  WELLS: "WELLS",
  CORPORATE: "CORPORATE",
};

const CLO_COLUMNS = {
  AAABSL: 14,
  AABSL: 15,
  ABSL: 16,
  BBBBSL: 17,
  BBBSL: 18,
  AAAMM: 41,
  AAMM: 42,
  AMM: 43,
  BBBMM: 44,
  BBMM: 45,
};

const cleanText = (value) =>
  value == null ? "" : String(value).replace(/\r\n?/g, "\n").trim();

const toIsoDate = (value) => {
  const date = coerceDate(value);
  const text = cleanText(date);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
};

const numeric = (value) => {
  const parsed = coerceNumeric(value);
  return parsed == null || !Number.isFinite(Number(parsed))
    ? null
    : Number(parsed);
};

const datedRows = (rows, startIndex, dateColumn) =>
  rows
    .slice(startIndex)
    .map((row) => ({ row: row || [], asofdt: toIsoDate(row?.[dateColumn]) }))
    .filter(({ asofdt }) => asofdt)
    .sort((left, right) => left.asofdt.localeCompare(right.asofdt));

const latestNumeric = (observations, column) => {
  for (let index = observations.length - 1; index >= 0; index -= 1) {
    const value = numeric(observations[index].row[column]);
    if (value != null) return value;
  }
  return null;
};

const parseBaml = (rows) => {
  const headers = rows[9] || [];
  const observations = datedRows(rows, 10, 0);
  if (!observations.length) {
    throw new Error("Master Table does not contain dated observations");
  }
  const asofdt = observations[observations.length - 1].asofdt;
  const parsedRows = headers
    .slice(1)
    .map((header, offset) => ({
      IDENTIFIER: cleanText(header),
      VALUE: latestNumeric(observations, offset + 1),
    }))
    .filter(({ IDENTIFIER, VALUE }) => IDENTIFIER && VALUE != null);
  return { sourceType: SOURCE_TYPES.BAML, asofdt, rows: parsedRows };
};

const parseClo = (rows) => {
  const observations = datedRows(rows, 12, 1);
  if (!observations.length) {
    throw new Error("1.1 Spreads does not contain dated observations");
  }
  const asofdt = observations[observations.length - 1].asofdt;
  const parsedRows = Object.entries(CLO_COLUMNS)
    .map(([IDENTIFIER, column]) => ({
      IDENTIFIER,
      VALUE: latestNumeric(observations, column),
    }))
    .filter(({ VALUE }) => VALUE != null);
  return { sourceType: SOURCE_TYPES.BAML_CLO, asofdt, rows: parsedRows };
};

// Wells publishes the report date twice: as "as of MM/DD/YYYY" text under the
// Summary title, and as a typed date on the TitlePage. Both are scanned for
// rather than read from fixed cells — the workbook has already shifted the
// title block once, and a hardcoded coordinate fails silently by reading a
// blank cell. The separator in the "as of" text is optional because Wells
// ships it malformed ("as of 0731/2026" in the 2026-07-31 report).
const AS_OF_PATTERN = /as of\s*(\d{1,2})\s*[/-]?\s*(\d{1,2})\s*[/-]\s*(\d{4})/i;

const findSummaryAsOfDate = (rows) => {
  for (const row of rows || []) {
    for (const cell of row || []) {
      const match = cleanText(cell).match(AS_OF_PATTERN);
      if (match) {
        const [, month, day, year] = match;
        return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      }
    }
  }
  return null;
};

const findFirstDate = (rows) => {
  for (const row of rows || []) {
    for (const cell of row || []) {
      const iso = toIsoDate(cell);
      if (iso) return iso;
    }
  }
  return null;
};

const parseWellsDate = (sheetsData) =>
  findSummaryAsOfDate(sheetsData.Summary) ||
  findFirstDate(sheetsData.TitlePage);

// Summary column layout, as published:
//   0 Sector (set once per group, blank on continuation rows)
//   1 Sub-Sector   2 Benchmark   3 Spread   4 WoW change   5 MoM change
// Wells quotes some rows in 32nds price ticks ("0-24", "-0-08") rather than
// spreads. coerceNumeric parses those with parseFloat, which truncates
// "0-24" to a very plausible-looking 0, so the spread column only accepts a
// genuine number or a clean numeric literal.
const NUMERIC_TEXT = /^-?\d+(\.\d+)?$/;

const strictNumeric = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const text = cleanText(value);
  return NUMERIC_TEXT.test(text) ? Number(text) : null;
};

const WELLS_SECTOR_COLUMN = 0;
const WELLS_SUB_SECTOR_COLUMN = 1;
const WELLS_SPREAD_COLUMN = 3;

// Locate the header rather than hardcoding a row so a shifted title block
// cannot silently push the reader onto the wrong rows.
const findWellsHeaderRow = (rows) =>
  (rows || []).findIndex(
    (row) =>
      cleanText(row?.[WELLS_SECTOR_COLUMN]).toLowerCase() === "sector" &&
      cleanText(row?.[WELLS_SUB_SECTOR_COLUMN]).toLowerCase() === "sub-sector",
  );

const parseWells = (sheetsData) => {
  const asofdt = parseWellsDate(sheetsData);
  if (!asofdt)
    throw new Error("Wells Summary does not contain a valid as-of date");

  const summary = sheetsData.Summary || [];
  const headerRow = findWellsHeaderRow(summary);
  if (headerRow < 0) {
    throw new Error(
      'Wells Summary does not contain a "Sector" / "Sub-Sector" header row',
    );
  }

  let sector = "";
  const parsedRows = [];
  for (const row of summary.slice(headerRow + 1)) {
    const rowSector = cleanText(row?.[WELLS_SECTOR_COLUMN]);
    if (rowSector) sector = rowSector;
    const subSector = cleanText(row?.[WELLS_SUB_SECTOR_COLUMN]);
    // Price-tick quotes are skipped, along with the footnote rows below the
    // table, which carry no sub-sector.
    const value = strictNumeric(row?.[WELLS_SPREAD_COLUMN]);
    if (sector && subSector && value != null) {
      parsedRows.push({ SECTOR: sector, SUB_SECTOR: subSector, VALUE: value });
    }
  }
  if (!parsedRows.length) {
    throw new Error("Wells Summary does not contain any spread rows");
  }
  return { sourceType: SOURCE_TYPES.WELLS, asofdt, rows: parsedRows };
};

const CORPORATE_BLOCKS = [
  { dateColumn: 0, valueColumn: 1 },
  { dateColumn: 4, valueColumn: 5 },
  { dateColumn: 7, valueColumn: 8 },
  { dateColumn: 10, valueColumn: 11 },
];

const parseCorporate = (rows) => {
  const parsedRows = [];
  const dates = [];
  for (const { dateColumn, valueColumn } of CORPORATE_BLOCKS) {
    const identifier = cleanText(rows[0]?.[valueColumn]);
    const observations = datedRows(rows, 6, dateColumn);
    const value = latestNumeric(observations, valueColumn);
    if (identifier && observations.length && value != null) {
      dates.push(observations[observations.length - 1].asofdt);
      parsedRows.push({ IDENTIFIER: identifier, VALUE: value });
    }
  }
  if (!parsedRows.length) {
    throw new Error(
      "Worksheet Values does not contain corporate spread observations",
    );
  }
  return {
    sourceType: SOURCE_TYPES.CORPORATE,
    asofdt: dates.sort().at(-1),
    rows: parsedRows,
  };
};

export const mapRelValSourceWorkbook = (sheetsData) => {
  if (sheetsData.Output) {
    throw new Error(
      'Unsupported workbook. Asset Menu "Output" sheet detected.',
    );
  }
  if (sheetsData["1.1 Spreads"]) {
    return parseClo(sheetsData["1.1 Spreads"]);
  }
  if (sheetsData["Master Table"]) {
    return parseBaml(sheetsData["Master Table"]);
  }
  if (sheetsData.Summary && (sheetsData.TitlePage || sheetsData.Disclosure)) {
    return parseWells(sheetsData);
  }
  if (sheetsData["Worksheet Values"]) {
    return parseCorporate(sheetsData["Worksheet Values"]);
  }
  throw new Error(
    'Unsupported workbook. Expected "Master Table", "1.1 Spreads", "Summary", or "Worksheet Values".',
  );
};

export const parseRelValSourceWorkbook = async (file) => {
  const { sheetsData } = await parseExcelWorkbook(file, {
    raw: true,
    defval: "",
  });
  const parsed = mapRelValSourceWorkbook(sheetsData);
  return { ...parsed, fileName: file?.name || null };
};
